import { useEffect, useRef } from 'react'
import type { VoiceState } from '../hooks/useVoice'
import type { Message } from '../lib/types'
import type { ExtractedItem } from '../lib/api'

/**
 * Receipt — the visible chat surface.
 *
 * Doctrine (2026-05-06 simplification, per Ryan):
 *   "We actually never should show the formatted text of the user
 *    response. It should just be the machine response in green at
 *    the top of the screen."
 *
 * So Receipt now renders ONE thing: the most-recent AGENT message,
 * in accent green, at the top of the chat region. User bubbles are
 * dropped entirely. The user's own utterances live transiently in
 * the live-transcript area below (during recording) and the
 * "Transcribing what you said…" line (during processing); they don't
 * persist as bubbles in the canvas because the canvas is for the
 * SYSTEM's voice — what's the prompt right now? — not a back-and-
 * forth transcript log.
 *
 * Props kept for API compatibility (suppressOldUser is now a no-op
 * since user bubbles don't render at all, but Home still passes it
 * — leaving it lets us add it back without a Home-side change).
 */

interface ReceiptProps {
  messages: Message[]
  voiceState: VoiceState
  /** No-op now (user bubbles never render). Kept for API stability. */
  suppressOldUser?: boolean
  item: Partial<ExtractedItem>
  reportType: 'lost' | 'found'
  /**
   * Optional retry callback for text-mode errors. Renders an inline
   * "Retry →" link when isError + this callback is provided. Per
   * round-1 critic P1: "no Retry / Resend button — the red text
   * reads as terminal."
   */
  onRetry?: () => void
  /**
   * Optional voice-retry callback for voice-mode errors. Renders an
   * inline "Try voice again ↻" link beside the error. Per round-3
   * critic P1: "voice-failure recovery is a paragraph, not a
   * button — phrased as a question, not a tap target."
   */
  onRetryVoice?: () => void
}

// Keyword highlighting on the agent prompt was tried and pulled per
// Ryan 2026-05-06: "in the green machine response you started
// highlighting key words, this hasn't been done for forever." The
// agent message renders as plain text now — its color (--accent
// green) carries enough "system speaking" signal on its own.

export default function Receipt({ messages, onRetry, onRetryVoice }: ReceiptProps) {
  const bottomRef = useRef<HTMLDivElement>(null)

  // Find the latest agent message — that's the only thing we render.
  const lastAgent = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'agent') return messages[i]
    }
    return null
  })()

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [lastAgent?.id])

  if (!lastAgent) return null

  return (
    <div className="w-full max-w-lg mx-auto px-4">
      {/* Latest agent prompt — accent green at the top of the chat
          region. 16px Inter weight 500 (matches the user-bubble size,
          per Ryan). Per Ryan 2026-05-06: "the machine response in
          green at the top of the screen." Error-flagged messages
          (mic permission denied, transcription failure, network
          error, submit failure) render in --error red instead, so
          the user can distinguish a system error from a normal
          agent prompt at a glance. */}
      <div
        key={lastAgent.id}
        className="flex flex-wrap items-baseline gap-x-3 animate-fade-in pr-6"
      >
        <span
          style={{
            color: lastAgent.isError ? 'var(--error)' : 'var(--accent)',
            fontFamily: 'inherit',
            fontStyle: 'normal',
            fontSize: '16px',
            lineHeight: 1.45,
            fontWeight: 500,
            transition: 'color 0.4s ease',
          }}
        >
          {lastAgent.text}
        </span>
        {/* Retry affordance — only on error AND only when a retry
            callback is wired (text-mode sends; voice failures don't
            retain the audio blob, so we skip retry there). */}
        {lastAgent.isError && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="hover:underline"
            style={{
              fontFamily: 'inherit',
              fontSize: 'var(--eyebrow-size)',
              fontWeight: 'var(--eyebrow-weight)',
              color: 'var(--error)',
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: 'var(--eyebrow-tracking)',
            }}
            aria-label="Retry the last message"
          >
            Retry →
          </button>
        )}
        {lastAgent.isError && onRetryVoice && (
          <button
            type="button"
            onClick={onRetryVoice}
            className="hover:underline"
            style={{
              fontFamily: 'inherit',
              fontSize: 'var(--eyebrow-size)',
              fontWeight: 'var(--eyebrow-weight)',
              color: 'var(--error)',
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: 'var(--eyebrow-tracking)',
            }}
            aria-label="Try voice again"
          >
            Try voice ↻
          </button>
        )}
      </div>
      <div ref={bottomRef} />
    </div>
  )
}
